import { useState, useEffect, useCallback, useRef } from 'react'
import { confirmAlert } from 'react-confirm-alert'
import 'react-confirm-alert/src/react-confirm-alert.css'
import { showSignedOutToast } from '../utils/sessionToast'
import { getStoredUserId, getStoredJwt } from '../utils/storedUserId'

export const DICTIONARY_RECURRING_COPY = {
    de: {
        cancelRenewal: 'Automatische Verlängerung kündigen',
        confirmTitle: 'Kündigung bestätigen',
        confirmMessage:
            'Die automatische Verlängerung Ihres Dictionary-Abonnements wird beendet. Sie behalten den Zugang bis zum Ende der bereits bezahlten Periode.',
        confirmProceed: 'Kündigen',
        confirmAbort: 'Abbrechen',
        processing: 'Wird verarbeitet…',
        err404: 'Es ist keine aktive wiederkehrende Zahlung vorhanden.',
        errGeneric: 'Die Anfrage konnte nicht ausgeführt werden. Bitte versuchen Sie es später erneut.',
        successPrefix: 'Verlängerung deaktiviert; Zugang bis',
    },
    en: {
        cancelRenewal: 'Cancel automatic renewal',
        confirmTitle: 'Confirm cancellation',
        confirmMessage:
            'Automatic renewal of your dictionary subscription will stop. You keep access until the end of the period you already paid for.',
        confirmProceed: 'Cancel renewal',
        confirmAbort: 'Back',
        processing: 'Processing…',
        err404: 'There is no active recurring payment.',
        errGeneric: 'The request could not be completed. Please try again later.',
        successPrefix: 'Renewal deactivated; access until',
    },
}

export function isDictionaryRecurringActive(check) {
    if (!check || typeof check !== 'object') return false
    const v = check.dictionary_recurring_active
    return v === true || v === 'true' || v === 1 || v === '1'
}

/** API puede devolver `{ response: { … } }` o `{ data: { … } }`. */
export function normalizeAuthCheckPayload(raw) {
    if (!raw || typeof raw !== 'object') return raw
    return {
        ...raw,
        ...(raw.response && typeof raw.response === 'object' ? raw.response : {}),
        ...(raw.data && typeof raw.data === 'object' ? raw.data : {}),
    }
}

/** Renovación automática desactivada y el backend indica cuándo se canceló. */
export function isDictionaryRecurringCancelledState(check) {
    if (!check || typeof check !== 'object') return false
    if (isDictionaryRecurringActive(check)) return false
    const at = check.dictionary_recurring_cancelled_at
    return at != null && String(at).trim() !== ''
}

/**
 * @returns {{ line1: string, line2: string | null } | null}
 */
export function getRecurringCancelledStatusLines(check, language) {
    if (!isDictionaryRecurringCancelledState(check)) return null
    const cancelled = formatDictionarySubscriptionUntil(
        check.dictionary_recurring_cancelled_at,
        language
    )
    const untilIso = check.dictionary_subscription_until
    const until = untilIso
        ? formatDictionarySubscriptionUntil(untilIso, language)
        : ''
    const de = String(language || '').toLowerCase() === 'de'
    if (de) {
        return {
            line1: `Die automatische Verlängerung wurde am ${cancelled} gekündigt.`,
            line2: until ? `Ihr Abonnement bleibt gültig bis ${until}.` : null,
        }
    }
    return {
        line1: `Automatic renewal was cancelled on ${cancelled}.`,
        line2: until ? `Your current subscription is valid until ${until}.` : null,
    }
}

export function formatDictionarySubscriptionUntil(iso, language) {
    if (!iso) return ''
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return String(iso)
    return d.toLocaleDateString(language === 'de' ? 'de-CH' : 'en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    })
}

/**
 * @param {{ language: string, history: { push: (loc: object) => void }, onAfterUnauthorized?: () => void }} opts
 */
export function useDictionaryRecurringCancel({ language, history, onAfterUnauthorized }) {
    const [authCheck, setAuthCheck] = useState(null)
    const [authCheckLoading, setAuthCheckLoading] = useState(false)
    const [authCheckFailed, setAuthCheckFailed] = useState(false)
    const [recurringCancelStatus, setRecurringCancelStatus] = useState(null)
    const [recurringCancelBusy, setRecurringCancelBusy] = useState(false)

    const onAfterUnauthorizedRef = useRef(onAfterUnauthorized)
    onAfterUnauthorizedRef.current = onAfterUnauthorized

    const dictRecurringCopy = useCallback(
        () => DICTIONARY_RECURRING_COPY[language === 'de' ? 'de' : 'en'],
        [language]
    )

    const forceLogoutSession = useCallback(() => {
        try {
            window.localStorage.removeItem('jwt')
            window.localStorage.removeItem('id')
            window.localStorage.removeItem('account')
            window.localStorage.removeItem('token')
        } catch (_e) {
            /* ignore */
        }
        onAfterUnauthorizedRef.current?.()
        showSignedOutToast(language)
        history.push({ pathname: '/' })
    }, [history, language])

    const refetchAuthCheck = useCallback(async () => {
        const token = getStoredJwt()
        const uid = getStoredUserId()
        if (!token) {
            setAuthCheck(null)
            return null
        }
        if (!uid) {
            setAuthCheck(null)
            setAuthCheckFailed(true)
            return null
        }
        setAuthCheckLoading(true)
        setAuthCheckFailed(false)
        try {
            const apiBase = process.env.REACT_APP_API_GOLANG_URL || ''
            const qs = new URLSearchParams({ user_id: uid })
            const res = await fetch(`${apiBase}/v1/auth/check?${qs.toString()}`, {
                method: 'GET',
                headers: { Authorization: `Bearer ${token}` },
            })
            if (res.status === 401) {
                forceLogoutSession()
                return null
            }
            // If the endpoint doesn't exist yet (404/500), fall back gracefully
            if (!res.ok) {
                setAuthCheck(null)
                setAuthCheckFailed(true)
                return null
            }
            const raw = await res.json().catch(() => ({}))
            const payload = normalizeAuthCheckPayload(raw)
            setAuthCheckFailed(false)
            setAuthCheck(payload)
            return payload
        } catch (_e) {
            setAuthCheck(null)
            setAuthCheckFailed(true)
            return null
        } finally {
            setAuthCheckLoading(false)
        }
    }, [forceLogoutSession])

    const executeDictionaryRecurringCancel = useCallback(async () => {
        const copy = dictRecurringCopy()
        const token = getStoredJwt()
        const uid = getStoredUserId()
        if (!token || !uid) return
        setRecurringCancelBusy(true)
        setRecurringCancelStatus(null)
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_GOLANG_URL}/v1/payment/dictionary/recurring/cancel`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ user_id: uid, token }),
                }
            )
            if (res.status === 401) {
                forceLogoutSession()
                return
            }
            if (res.status === 404) {
                setRecurringCancelStatus({ type: 'error', message: copy.err404 })
                await refetchAuthCheck()
                return
            }
            if (!res.ok) {
                let msg = copy.errGeneric
                try {
                    const errBody = await res.json()
                    const detail = errBody?.error ?? errBody?.message ?? errBody?.detail
                    if (detail != null && detail !== '') {
                        msg = typeof detail === 'string' ? detail : copy.errGeneric
                    }
                } catch (_e) {
                    /* keep generic */
                }
                setRecurringCancelStatus({ type: 'error', message: msg })
                return
            }
            const refreshed = await refetchAuthCheck()
            const untilIso = refreshed?.dictionary_subscription_until
            const untilLabel = untilIso
                ? formatDictionarySubscriptionUntil(untilIso, language)
                : ''
            const successMsg = untilLabel
                ? `${copy.successPrefix} ${untilLabel}.`
                : `${copy.successPrefix}.`
            setRecurringCancelStatus({ type: 'success', message: successMsg })
        } catch (_e) {
            setRecurringCancelStatus({
                type: 'error',
                message: copy.errGeneric,
            })
        } finally {
            setRecurringCancelBusy(false)
        }
    }, [dictRecurringCopy, forceLogoutSession, language, refetchAuthCheck])

    const promptCancelDictionaryRecurring = useCallback(() => {
        const copy = dictRecurringCopy()
        confirmAlert({
            title: copy.confirmTitle,
            message: copy.confirmMessage,
            buttons: [
                { label: copy.confirmAbort, onClick: () => {} },
                {
                    label: copy.confirmProceed,
                    onClick: () => {
                        void executeDictionaryRecurringCancel()
                    },
                },
            ],
        })
    }, [dictRecurringCopy, executeDictionaryRecurringCancel])

    useEffect(() => {
        if (!getStoredJwt()) return undefined
        void refetchAuthCheck()
        return undefined
    }, [language, refetchAuthCheck])

    return {
        authCheck,
        authCheckLoading,
        authCheckFailed,
        recurringCancelStatus,
        recurringCancelBusy,
        dictRecurringCopy,
        promptCancelDictionaryRecurring,
        executeCancel: executeDictionaryRecurringCancel,
        refetchAuthCheck,
    }
}
